import { ChangeDetectionStrategy, Component, ViewChild } from '@angular/core';
import { I18NextPipe } from 'angular-i18next';
import { ButtonComponent, ModalComponent, ModalFooterDirective } from 'mbs-ui-kit';
import { HelpLinkPipe } from 'mbs-ui-shared@help/helpLink.pipe';
import { getForceUpdateBuildOsKey } from '../computers-force-update-modal.constants';
import { BuildAction, BuildActionOffer, NewVersionEntry } from '../force-update-new-version-utils/force-update-new-version';

export type ForceUpdateActionConfirmData = {
  offer: BuildActionOffer;
  productKey: string;
  showReleaseNotes: boolean;
  isAutoUpdateEnabled: boolean;
};

const actionKeys: Record<BuildAction, { action: string; text: string }> = {
  [BuildAction.GenerateFirst]: { action: 'shared:buttons.generate', text: 'shared:forceUpdate.actionConfirm.generateFirstText' },
  [BuildAction.MakePublic]: {
    action: 'shared:download-build.versions.makePublic',
    text: 'shared:forceUpdate.actionConfirm.makePublicText'
  },
  [BuildAction.GenerateSandbox]: {
    action: 'shared:download-build.versions.generateSandbox',
    text: 'shared:forceUpdate.actionConfirm.sandboxText'
  },
  [BuildAction.Generate]: { action: 'shared:buttons.generate', text: 'shared:forceUpdate.actionConfirm.publicText' }
};

@Component({
  selector: 'mbs-force-update-action-confirm-modal',
  templateUrl: './force-update-action-confirm-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonComponent, HelpLinkPipe, I18NextPipe, ModalComponent, ModalFooterDirective]
})
export class ForceUpdateActionConfirmModalComponent {
  @ViewChild(ModalComponent, { static: true }) baseModal: ModalComponent;

  public readonly elementSelectors = {
    name: {
      row: 'force-update-action-confirm-row',
      version: 'force-update-action-confirm-version',
      autoUpdateWarning: 'force-update-action-confirm-auto-update-warning',
      confirmButton: 'force-update-action-confirm-button',
      cancelButton: 'force-update-action-cancel-button'
    }
  };

  public get data(): ForceUpdateActionConfirmData {
    return this.baseModal.data as ForceUpdateActionConfirmData;
  }

  private get keys(): { action: string; text: string } {
    return actionKeys[this.data?.offer?.action] ?? actionKeys[BuildAction.Generate];
  }

  public get actionKey(): string {
    return this.keys.action;
  }

  public get textKey(): string {
    return this.keys.text;
  }

  public get entries(): (NewVersionEntry & { osKey: string })[] {
    return (this.data?.offer?.entries ?? []).map((entry) => ({ ...entry, osKey: getForceUpdateBuildOsKey(entry.buildType, entry.os) }));
  }

  public get versionsCount(): number {
    return this.data?.offer?.entries.length ?? 0;
  }

  public get showAutoUpdateWarning(): boolean {
    return this.data?.offer?.action !== BuildAction.GenerateSandbox && !!this.data?.isAutoUpdateEnabled;
  }

  public confirm(): void {
    this.baseModal.save(true);
  }

  public cancel(): void {
    this.baseModal.close();
  }
}
